import {
  ImageUniDriver,
  InputUniDriver,
  ToggleSwitchUniDriver,
} from '@wix/design-system/dist/testkit/unidriver';
import type { GetOptions, Testkit, UniDriver } from 'ricos-types/testkit';
import { dataHook, RicosPluginToolbarNames } from 'wix-rich-content-common';
import { INSERT_PLUGIN_BUTTONS } from 'wix-rich-content-editor-common';
import type { FooterActionsUnidrver } from 'wix-rich-content-ui-components/testkit';
import { createFooterActionsUnidriver } from 'wix-rich-content-ui-components/testkit';

import {
  ROOT,
  SETTINGS_ALT_INPUT_TEXT,
  SETTINGS_CAPTION_INPUT,
  SETTINGS_DISABLE_DOWNLOAD_TOGGLE,
  SETTINGS_DISABLE_EXPAND_TOGGLE,
  SETTINGS_IMAGE_PREVIEW,
  SETTINGS_SPOILER_TOGGLE,
} from './dataHooks';
import { imageAddToolbars } from './getAddButtons';

export type ImageTestkit = Testkit<{
  content: {
    atIndex: (index: number) => {
      exists: (options?: GetOptions) => Promise<boolean>;
      click: () => Promise<void>;
      getText: () => Promise<string>;
      getAlt: () => Promise<string>;
      hasSpoiler: () => Promise<boolean>;
      getStyle: () => Promise<string | null>;
    };
  };
  settings: {
    image: ReturnType<typeof ImageUniDriver>;
    disableExpandToggle: ReturnType<typeof ToggleSwitchUniDriver>;
    disableDownloadToggle: ReturnType<typeof ToggleSwitchUniDriver>;
    spoilerToggle: ReturnType<typeof ToggleSwitchUniDriver>;
    captionInput: ReturnType<typeof InputUniDriver> & { waitForElement: () => Promise<void> };
    altTextInput: ReturnType<typeof InputUniDriver>;
  } & FooterActionsUnidrver;
  toolbars: {
    createAddButtonToolbars: (getAddButtonBase: () => UniDriver<Element>) => {
      click: () => Promise<void>;
      toolbarsToAddIn: typeof imageAddToolbars;
    };
    createPluginToolbar: (
      getPluginToolbarBase: (pluginType: typeof RicosPluginToolbarNames.IMAGE) => UniDriver<Element>
    ) => {
      openSettingsModal: () => Promise<void>;
      clickDelete: () => Promise<void>;
      openLinkPanel: () => Promise<void>;
      clickSizeDropdown: () => Promise<void>;
      imageSize: {
        clickSmall: () => Promise<void>;
      };
      exists: () => Promise<boolean>;
    };
  };
}>;

export const createImageTestkit: ImageTestkit = getBase => {
  const getImage = async (index: number, options?: GetOptions) => {
    const image = await getBase().getAll(dataHook(ROOT), options);
    return image.at(index);
  };
  const getImageTag = async () => await getBase().get(`${dataHook(ROOT)} img`);

  return {
    content: {
      atIndex: index => {
        return {
          exists: async options => {
            try {
              const image = await getImage(index, options);
              return image.exists();
            } catch (error) {
              return false;
            }
          },
          click: async () => {
            const image = await getImage(index);
            await image.click();
          },
          getText: async () => (await getImage(index)).text(),
          getAlt: async () => (await getImageTag())._prop('alt'),
          hasSpoiler: async () => {
            try {
              const spoiler = await getBase().get(dataHook('spoilerContainer'));
              return spoiler.exists();
            } catch (error) {
              return false;
            }
          },
          getStyle: async () => (await getImageTag()).attr('style'),
        };
      },
    },
    settings: {
      image: ImageUniDriver(getBase().$(dataHook(SETTINGS_IMAGE_PREVIEW)), getBase()),
      disableExpandToggle: ToggleSwitchUniDriver(
        getBase().$(dataHook(SETTINGS_DISABLE_EXPAND_TOGGLE)),
        getBase()
      ),
      disableDownloadToggle: ToggleSwitchUniDriver(
        getBase().$(dataHook(SETTINGS_DISABLE_DOWNLOAD_TOGGLE)),
        getBase()
      ),
      spoilerToggle: ToggleSwitchUniDriver(
        getBase().$(dataHook(SETTINGS_SPOILER_TOGGLE)),
        getBase()
      ),
      captionInput: {
        waitForElement: async () => {
          await getBase().get(dataHook(SETTINGS_CAPTION_INPUT));
        },
        ...InputUniDriver(getBase().$(dataHook(SETTINGS_CAPTION_INPUT)), getBase()),
      },
      altTextInput: InputUniDriver(getBase().$(dataHook(SETTINGS_ALT_INPUT_TEXT)), getBase()),
      ...createFooterActionsUnidriver(getBase),
    },
    toolbars: {
      createAddButtonToolbars: getAddButtonBase => ({
        click: async () => {
          const imagePluginButton = await getAddButtonBase().get(
            dataHook(INSERT_PLUGIN_BUTTONS.IMAGE)
          );
          await imagePluginButton.click();
        },
        toolbarsToAddIn: imageAddToolbars,
      }),
      createPluginToolbar: getPluginToolbarBase => ({
        openSettingsModal: async () => {
          const settings = await getPluginToolbarBase(RicosPluginToolbarNames.IMAGE).get(
            dataHook('baseToolbarButton_settings')
          );
          await settings.click();
        },
        clickDelete: async () => {
          const button = await getPluginToolbarBase(RicosPluginToolbarNames.IMAGE).get(
            dataHook('blockButton_delete')
          );
          await button.click();
        },
        openLinkPanel: async () => {
          const button = await getPluginToolbarBase(RicosPluginToolbarNames.IMAGE).get(
            dataHook('LinkButton')
          );
          await button.click();
        },
        clickSizeDropdown: async () => {
          const dropdown = await getPluginToolbarBase(RicosPluginToolbarNames.IMAGE).get(
            dataHook('nodeSizeButton')
          );
          await dropdown.click();
        },
        imageSize: {
          clickSmall: async () => {
            const allignmentButton = await getBase().get(
              dataHook('blockAlignmentAndSizeButton_sizeSmallCenter')
            );
            await allignmentButton.click();
          },
        },
        exists: async () => {
          try {
            return getPluginToolbarBase(RicosPluginToolbarNames.IMAGE).exists();
          } catch (error) {
            return false;
          }
        },
      }),
    },
  };
};
